/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import {useItemVariant} from '.'
import Link from '@salesforce/retail-react-app/app/components/link'
import {Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import {productUrlBuilder} from '../../utils/url'
import {useLocation} from 'react-router-dom'
import {sendClickEvent, sendSelectItemEvent, INTERACTION_LOCATION} from '../../utils/gtm-utils'

/**
 * In the context of a cart product item variant, this components simply renders
 * the item's name. The variant `name` comes from a locale-aware product fetch, while the
 * cart item field `productName` is frozen in the locale the item was added in — so `name`
 * wins and `productName` is only the fallback while the product detail is still loading
 * (or when the product is no longer available).
 */
const ItemName = ({isMiniCart = false, index = 0, ...props}) => {
    const styles = useMultiStyleConfig('Cart')
    const location = useLocation()
    const variant = useItemVariant()
    const productName = variant?.name || variant?.productName
    const isGiftCard = variant?.c_isGiftCard || variant?.c_isEGiftCard
    const isCheckout = /\/checkout$/.test(location?.pathname)

    const sendGTMEvent = () => {
        sendClickEvent('cart_view_product_clicked')
        sendSelectItemEvent(
            variant,
            undefined,
            Number.isFinite(Number(index)) ? Number(index) : 0,
            isMiniCart ? INTERACTION_LOCATION.MINI_CART : INTERACTION_LOCATION.CART
        )
    }

    return (
        <Text data-testid="product-item-name" {...styles.cartItemNameText}>
            {isGiftCard || isCheckout ? (
                productName
            ) : (
                <Link
                    {...styles.cartItemNameLink}
                    to={productUrlBuilder(variant)}
                    onClick={sendGTMEvent}
                    data-testid="product-name"
                    {...props}
                >
                    {productName}
                </Link>
            )}
        </Text>
    )
}

export default ItemName

ItemName.propTypes = {
    isMiniCart: PropTypes.bool,
    index: PropTypes.number
}
